/**
 * One panel that tells the owner: Brunel needs you, and what for.
 *
 * Read-only about the world, active only through the callbacks the parent
 * supplies: approve, reject, edit, take control, view Worker. The panel runs
 * nothing itself - it never touches a terminal, a plan or the database. What
 * Brunel wants to do next is written out in full before any button, so a
 * consequential approval is never hidden behind a vague word.
 */
import type { ReactElement, ReactNode } from 'react';
import { approveLabel, approvePayload, hasEditableInstruction, hasMeaningfulProposal, ownerAttentionLook } from '../lib/owner-attention-view';
import '../owner-attention-panel.css';

export interface OwnerAttentionPanelProps {
  /** The attention kind, from the planner's own vocabulary. */
  readonly kind: string;
  /** The reason this needs the owner, in the parent's own words. */
  readonly title: string;
  /** The originating Worker, already formatted, when one exists. */
  readonly workerLabel?: string | null;
  /** The planner or run reference, already formatted, when one exists. */
  readonly planLabel?: string | null;
  /** The current outcome or state, in words. */
  readonly outcomeLabel?: string | null;
  /** What Brunel wants to do next, in plain words. */
  readonly proposedAction?: string | null;
  /** Why this needs attention. */
  readonly explanation?: string | null;
  /** When this happened, already formatted by the parent. */
  readonly timestampLabel?: string | null;
  /** The proposed instruction, editable when supplied. */
  readonly proposedInstruction?: string | null;
  /** The owner edit so far, owned by the parent; typing sends it here. */
  readonly draft?: string | null;
  /** Called when the owner types into the editable instruction. */
  readonly onDraftChange?: (value: string) => void;
  /** The composed animated status indicator, when the surface wants one. */
  readonly indicator?: ReactNode;
  /** True while the parent is carrying out an action. */
  readonly busy?: boolean;
  /** Approving sends the proposed (or owner-edited) instruction onward. */
  readonly onApprove?: (instruction: string | null) => void;
  /** Declining - nothing is sent. */
  readonly onReject?: () => void;
  /** Ask the parent to open the instruction for a bigger edit. */
  readonly onEdit?: () => void;
  /** Take the Worker's keyboard. */
  readonly onTakeControl?: () => void;
  /** Open the Worker's surface. */
  readonly onViewWorker?: () => void;
}

export function OwnerAttentionPanel({
  kind,
  title,
  workerLabel,
  planLabel,
  outcomeLabel,
  proposedAction,
  explanation,
  timestampLabel,
  proposedInstruction,
  draft,
  onDraftChange,
  indicator,
  busy = false,
  onApprove,
  onReject,
  onEdit,
  onTakeControl,
  onViewWorker,
}: OwnerAttentionPanelProps): ReactElement {
  const look = ownerAttentionLook(kind);
  const hasProposal = hasMeaningfulProposal(proposedAction, proposedInstruction);
  const editableInstruction = hasEditableInstruction(proposedInstruction);

  const facts = [
    workerLabel ?? null,
    planLabel ?? null,
    outcomeLabel ?? null,
    timestampLabel ?? null,
  ].filter((fact): fact is string => fact !== null && fact !== '');

  const actions: ReadonlyArray<{ readonly label: string; readonly onAct: () => void; readonly primary: boolean }> = [
    ...(onApprove ? [{ label: approveLabel(hasProposal), onAct: () => onApprove(approvePayload(proposedInstruction ?? null, draft ?? null)), primary: true }] : []),
    ...(onReject ? [{ label: 'Reject proposal', onAct: onReject, primary: false }] : []),
    ...(onEdit && editableInstruction ? [{ label: 'Edit instruction', onAct: onEdit, primary: false }] : []),
    ...(onTakeControl ? [{ label: 'Take control', onAct: onTakeControl, primary: false }] : []),
    ...(onViewWorker ? [{ label: 'View Worker', onAct: onViewWorker, primary: false }] : []),
  ];

  return (
    <section
      className={`owner-attention owner-attention--${look.tone}`}
      role="region"
      aria-label={`${look.heading}. ${title}`}
      aria-live="polite"
      data-attention-kind={look.key}
    >
      <div className="owner-attention-head">
        {indicator}
        <h3>{look.heading}</h3>
      </div>
      <p className="owner-attention-title">{title}</p>
      {facts.length > 0 && <div className="owner-attention-facts">{facts.map((fact) => <span key={fact}>{fact}</span>)}</div>}
      {proposedAction !== null && proposedAction !== undefined && proposedAction !== '' && (
        <div className="owner-attention-proposal">
          <p className="owner-attention-proposal-label">What Brunel wants to do next</p>
          <p className="owner-attention-proposal-text">{proposedAction}</p>
        </div>
      )}
      {explanation !== null && explanation !== undefined && explanation !== '' && (
        <p className="owner-attention-explanation">{explanation}</p>
      )}
      {editableInstruction && (
        <div className="owner-attention-instruction">
          <label>
            Proposed instruction (editable)
            <textarea
              value={draft ?? proposedInstruction ?? ''}
              onChange={(event) => onDraftChange?.(event.target.value)}
              rows={4}
            />
          </label>
          <p className="owner-attention-hint">Editing changes nothing until you approve.</p>
        </div>
      )}
      {actions.length > 0 && (
        <div className="owner-attention-actions">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              className={action.primary ? 'primary' : 'ghost'}
              disabled={busy}
              aria-label={action.label}
              onClick={action.onAct}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
